/**
 * SignUpGate - Modal that prompts guest users to sign up
 * 
 * Appears when guests try to access premium features:
 * - Clicking on result cards to see full details
 * - Accessing contact information
 * - Exporting results
 * - Saving searches
 * 
 * Value-first approach: Shows what they'll get after signing up.
 */

import React from 'react';
import {
  X,
  Lock,
  ArrowRight,
  Mail,
  Phone,
  Building2,
  CheckCircle,
  Sparkles,
  Zap,
  Target,
  Download,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// Feature highlights to show in the gate
const UNLOCK_FEATURES = [
  { icon: Mail, text: 'Verified email addresses' },
  { icon: Phone, text: 'Direct phone numbers' },
  { icon: Building2, text: 'Complete company profiles' },
  { icon: Target, text: 'AI-powered search' },
  { icon: Download, text: 'Export to CSV' },
  { icon: Zap, text: 'Save & track searches' },
];

// Feature-specific messaging
const FEATURE_MESSAGES = {
  search: {
    title: 'Start finding anyone',
    subtitle: 'Create a free account to search',
    icon: Target,
  },
  default: {
    title: 'Unlock full access',
    subtitle: 'Create a free account to continue',
    icon: Lock,
  },
};

export function SignUpGateModal({ isOpen, onClose, feature = 'view full profiles' }) {
  const { loginWithRedirect } = useAuth();
  
  if (!isOpen) return null;
  
  // Get feature-specific messaging
  const messaging = FEATURE_MESSAGES[feature] || FEATURE_MESSAGES.default;
  const HeaderIcon = messaging.icon;
  
  const handleSignUp = () => {
    loginWithRedirect({
      appState: { returnTo: '/app' },
      authorizationParams: {
        screen_hint: 'signup',
      },
    });
  };
  
  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: '/app' },
    });
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Premium glass effect */}
      <div 
        className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        style={{ 
          animation: 'eq-scale-in 300ms cubic-bezier(0.16, 1, 0.3, 1)',
          fontFamily: "'Playfair Display', serif",
        }}
      >
        <style>{`
          @keyframes eq-scale-in {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}</style>
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/50 transition-colors z-10"
        >
          <X className="w-5 h-5 text-white/70" />
        </button>
        
        {/* Header gradient - More welcoming */}
        <div className="bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] p-8 text-white text-center relative overflow-hidden">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px),
                              radial-gradient(circle at 80% 50%, white 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }} />
          
          <div className="relative z-10">
            <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-5 shadow-lg">
              <HeaderIcon className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {messaging.title}
            </h2>
            <p className="text-sm text-white/80">
              {messaging.subtitle}
            </p>
          </div>
        </div>
        
        {/* Features list - Clean grid */}
        <div className="p-6">
          <p className="text-sm font-medium text-black/50 mb-4 text-center">
            What you get with a free account:
          </p>
          
          <div className="grid grid-cols-2 gap-3">
            {UNLOCK_FEATURES.map((feat, i) => {
              const Icon = feat.icon;
              return (
                <div 
                  key={i} 
                  className="flex items-center gap-2.5 p-2.5 rounded-xl bg-black/[0.02] hover:bg-black/[0.04] transition-colors"
                >
                  <div className="w-7 h-7 rounded-lg bg-[#5a20ff]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[#5a20ff]" />
                  </div>
                  <span className="text-sm text-black/70">{feat.text}</span>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* CTAs - More prominent */}
        <div className="px-6 pb-6 space-y-3">
          <button
            onClick={handleSignUp}
            className="w-full px-6 py-4 bg-[#5a20ff] text-white rounded-full font-semibold text-base hover:bg-[#4a1ad6] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-[#5a20ff]/30 hover:shadow-xl hover:scale-[1.02]"
          >
            <Sparkles className="w-5 h-5" />
            Start for free
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <button
            onClick={handleLogin}
            className="w-full px-6 py-3 text-black/60 hover:text-black font-medium text-sm transition-colors"
          >
            Already have an account? <span className="text-[#5a20ff]">Log in</span>
          </button>
        </div>
        
        {/* Trust note - Subtle */}
        <div className="px-6 pb-6 text-center border-t border-black/5 pt-4">
          <p className="text-xs text-black/40 flex items-center justify-center gap-2">
            <CheckCircle className="w-3 h-3 text-[#52dcb3]" />
            No credit card required
          </p>
        </div>
      </div>
    </div>
  );
}

// Inline sign-up prompt (for use within cards)
export function SignUpPromptInline({ feature = 'unlock', onSignUp, compact = false }) {
  const { loginWithRedirect } = useAuth();
  
  const handleClick = () => {
    if (onSignUp) {
      onSignUp();
    } else {
      loginWithRedirect({
        appState: { returnTo: '/app' },
        authorizationParams: {
          screen_hint: 'signup',
        },
      });
    }
  };
  
  if (compact) {
    return (
      <button
        onClick={handleClick}
        className="text-xs text-[#5a20ff] hover:text-[#4a1ad6] font-medium flex items-center gap-1"
      >
        <Lock className="w-3 h-3" />
        Sign up to {feature}
      </button>
    );
  }
  
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5 border border-[#5a20ff]/10">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center flex-shrink-0">
        <Lock className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-black">Sign up to {feature}</p>
        <p className="text-xs text-black/50">Free account, no credit card</p>
      </div>
      <button
        onClick={handleClick}
        className="px-4 py-2 bg-[#5a20ff] text-white rounded-full text-xs font-semibold hover:bg-[#4a1ad6] transition-colors flex-shrink-0"
      >
        Start Free
      </button>
    </div>
  );
}

// Hook to check if sign-up gate should be shown
export function useSignUpGate() {
  const { isAuthenticated, isLoading } = useAuth();
  const [showGate, setShowGate] = React.useState(false);
  const [gateFeature, setGateFeature] = React.useState('view full profiles');
  
  const requireAuth = React.useCallback((feature = 'view full profiles') => {
    // Don't wait for loading - if not authenticated, show the gate
    // This ensures the gate appears immediately for unauthenticated users
    if (!isAuthenticated && !isLoading) {
      setGateFeature(feature);
      setShowGate(true);
      return true; // Blocked, gate shown
    }
    
    // If still loading but likely not authenticated, show gate anyway
    if (isLoading) {
      setGateFeature(feature);
      setShowGate(true);
      return true;
    }
    
    return false; // Not blocked, user is authenticated
  }, [isAuthenticated, isLoading]);
  
  const closeGate = React.useCallback(() => {
    setShowGate(false);
  }, []);
  
  return {
    isGuest: !isAuthenticated && !isLoading,
    isLoading,
    showGate,
    gateFeature,
    requireAuth,
    closeGate,
    SignUpGate: () => (
      <SignUpGateModal 
        isOpen={showGate} 
        onClose={closeGate}
        feature={gateFeature}
      />
    ),
  };
}

export default SignUpGateModal;

